import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useUIStore } from '@/stores/use-ui-store';
import { events } from '@/lib/platform/generated/bindings.gen';
import { isDesktop as isDesktopDefault } from '@/lib/platform';

export interface NativeMenuBridgeOptions {
  /** Whether running inside Tauri desktop. Injected for testability. */
  isDesktop?: boolean;
}

/**
 * Listens for native menu events emitted by the Rust app_menu plugin and
 * routes them to the appropriate UI state. Mount once near the root of the app.
 *
 * Events handled:
 *  open-settings  — emitted when the user selects Nula > Settings… (⌘,)
 *
 * ⌘W and ⌘H are handled entirely by the native menu on the Rust side
 * (PredefinedMenuItem::close_window / PredefinedMenuItem::hide) and require
 * no JS bridge — they operate directly on the NSWindow / NSApplication.
 */
export function useNativeMenuBridge(options: NativeMenuBridgeOptions = {}): void {
  const { isDesktop = isDesktopDefault } = options;
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);

  useEffect(() => {
    if (!isDesktop) return;

    let unlisten: (() => void) | null = null;

    (async () => {
      try {
        unlisten = await events.openSettings.listen(() => {
          setSettingsOpen(true);
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('[NativeMenuBridge] Failed to setup open-settings listener:', error);
      }
    })();

    return () => {
      unlisten?.();
    };
  }, [isDesktop, setSettingsOpen]);
}
